import type { ReactNode } from 'react'

// Workbench drawing-tool icons — TradingView style: thin strokes with hollow
// circle handles. lucide has no equivalents, so these are hand-drawn in the same
// API (size / strokeWidth / className) as lucide icons. Circles are hollow (fill
// none); the connecting line runs to each circle's centre, reading as a ringed
// endpoint.

interface IconProps {
  size?: number | string
  strokeWidth?: number | string
  className?: string
}

function Svg({
  size = 16,
  strokeWidth = 1.75,
  className,
  children,
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {children}
    </svg>
  )
}

// Trend line — diagonal segment with a handle at each end.
export function TrendlineIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="5.5" y1="17.5" x2="18.5" y2="6.5" />
      <circle cx="5" cy="18" r="2.3" />
      <circle cx="19" cy="6" r="2.3" />
    </Svg>
  )
}

// Fibonacci retracement — stacked level lines with the two diagonal anchor handles.
export function FibonacciIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
      <circle cx="19.5" cy="6" r="2" />
      <circle cx="4.5" cy="18" r="2" />
    </Svg>
  )
}

// Horizontal line — full-width line with a centre handle.
export function HorizontalLineIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="3" y1="12" x2="21" y2="12" />
      <circle cx="12" cy="12" r="2.3" />
    </Svg>
  )
}

// Vertical line — full-height line with a centre handle.
export function VerticalLineIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="12" y1="3" x2="12" y2="21" />
      <circle cx="12" cy="12" r="2.3" />
    </Svg>
  )
}

// Parallel channel — two parallel diagonals, each end handled.
export function ChannelIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="5.5" y1="12.5" x2="16.5" y2="5.5" />
      <line x1="7.5" y1="18.5" x2="18.5" y2="11.5" />
      <circle cx="5" cy="13" r="1.7" />
      <circle cx="17" cy="5" r="1.7" />
      <circle cx="7" cy="19" r="1.7" />
      <circle cx="19" cy="11" r="1.7" />
    </Svg>
  )
}

// Long position — box split into a (larger) target zone above the entry line and
// a stop zone below, with an up arrow in the target zone. Monochrome.
export function LongPositionIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="4" y="4" width="16" height="16" rx="1.5" />
      <line x1="4" y1="13" x2="20" y2="13" />
      <path d="M12 11 V6.5 M9.6 8.9 L12 6.4 L14.4 8.9" />
    </Svg>
  )
}

// Short position — entry line high, stop zone above, target zone below, down arrow.
export function ShortPositionIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="4" y="4" width="16" height="16" rx="1.5" />
      <line x1="4" y1="11" x2="20" y2="11" />
      <path d="M12 13 V17.5 M9.6 15.1 L12 17.6 L14.4 15.1" />
    </Svg>
  )
}

// Rectangle — box with a handle at each corner.
export function RectangleIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="6" y="7" width="12" height="10" rx="0.5" />
      <circle cx="6" cy="7" r="1.8" />
      <circle cx="18" cy="7" r="1.8" />
      <circle cx="6" cy="17" r="1.8" />
      <circle cx="18" cy="17" r="1.8" />
    </Svg>
  )
}

// ---- Trade-idea roles -------------------------------------------------------
// The same three geometries the `tradeMark` overlay draws on the canvas, so the
// strip, the panel and the chart all say "stop loss" with one shape. Outline,
// never filled: at this size a filled circle and a filled diamond are the same
// coloured dot.

// Entry — hollow circle.
export function EntryCircleIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="7" />
    </Svg>
  )
}

// Stop loss — a cross. Nothing else in this set is an X.
export function StopCrossIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <line x1="5.5" y1="5.5" x2="18.5" y2="18.5" />
      <line x1="18.5" y1="5.5" x2="5.5" y2="18.5" />
    </Svg>
  )
}

// Take profit — hollow diamond.
export function TakeProfitDiamondIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 4 L20 12 L12 20 L4 12 Z" />
    </Svg>
  )
}
